import { useState, useEffect, useCallback } from "react";
import { ReactComponent as Bars } from "./../../../images/icons/SVG/bars.svg";
import ColorPicker, { useColorPicker } from "react-best-gradient-color-picker";
import { hexToRGB } from "./Colors_Utils";
import { sortLeftRightTopBottom } from "../../../refractCommon/miro/sortLeftRightTopBottom";
import {
  parseGradient,
  getItemPositionArray,
  getColorFromGradientWithPercentPosition,
  RGBToHex,
} from "./Colors_Utils";
import AnimateHeight from "react-animate-height";
import { generateUID } from "../../Utils/generateUID";
import { convertObjectToData } from "../../../refractCommon/utils/convertObjectToData";
import ToolTip from "../../../refractCommon/components/ToolTip";
import { filter } from "lodash";
import debounce from "lodash/debounce";

const miro = window.miro;

export default function Gradient({
  item,
  dataPath,
  dispatch,
  boardSelection,
  dragHandleProps,
}) {
  const [color, setColor] = useState("");
  const [nameFormValue, setNameFormValue] = useState(item.name);
  const [editMode, setEditMode] = useState(false);

  const [
    canFillSomeBoardItemsWithGradient,
    setCanFillSomeBoardItemsWithGradient,
  ] = useState(false);

  const { deletePoint, selectedPoint } = useColorPicker(
    item.gradient_colors,
    handleSetGradientColor
  );
  const [canImportFromBoardSelection, setCanImportFromBoardSelection] =
    useState(false);

  useEffect(() => {
    checkSelectionAndSetCapabilityState();
  }, [boardSelection]);

  async function checkSelectionAndSetCapabilityState() {
    let selection = await miro.board.getSelection();
    if (selection.length < 1) {
      setCanImportFromBoardSelection(false);
      return;
    }
    if (selection.length === 1) {
      if (selection[0].type === "shape") {
        setCanImportFromBoardSelection(false);
      }
    }
    if (selection.length > 1) {
      let types = selection.map((item) => item.type);
      if (types.includes("shape")) {
        setCanImportFromBoardSelection(true);
      }
    }
  }

  async function duplicateItem() {
    const newItem = JSON.parse(JSON.stringify(item));
    newItem.id = generateUID();
    newItem.name = "Copy of " + newItem.name;
    await dispatch([
      {
        path: [dataPath, newItem.id],
        data: convertObjectToData(newItem),
      },
      {
        path: [dataPath, "_order"],
        data: newItem.id,
        index: 0,
      },
    ]);
  }

  useEffect(() => {
    setNameFormValue(item.name);
  }, [item]);

  function handleSetEditMode() {
    setEditMode(!editMode);
  }

  useEffect(async () => {
    let selection = await miro.board.getSelection();

    if (selection.length > 0) {
      let types = selection.map((item) => item.type);
      if (types.includes("shape")) {
        setCanFillSomeBoardItemsWithGradient(true);
      }
    } else {
      setCanFillSomeBoardItemsWithGradient(false);
    }
  }, [boardSelection]);

  /// GRADIENT S T U F F        ////////////////////////////

  const handleNameChange = (e) => {
    const { value } = e.target;
    setNameFormValue(value);
    handleSaveName(value);
  };

  const handleSaveName = useCallback(
    debounce(async (value) => {
      await dispatch({
        path: dataPath + "." + item.id + ".name",
        data: value,
      });
    }, 500),
    []
  );

  function handleSetGradientColor(value) {
    dispatch({
      path: dataPath + "." + item.id + ".gradient_colors",
      data: value,
    });
  }

  function handleRemoveItem(item) {
    dispatch({
      path: [dataPath, item.id],
      remove: true,
    });
  }

  // GRADIENT FILL ACTION
  async function fillItemsWithGradient(grad) {
    // LETS GET THE GRADIENT IN A PARSED ARRAY WE CAN WORK WITH
    let gradientArray = parseGradient(grad);

    // NOW LETS WORK WITH THE GRADIENT AND THE BOARD SELECTION
    let selectionArray = await miro.board.getSelection();
    let filteredArray = selectionArray.filter((item) => item.type === "shape");
    let sortedLeftRightItemArray = sortLeftRightTopBottom(filteredArray);
    let itemPositionArray = getItemPositionArray(sortedLeftRightItemArray);

    let itemColorArray = [];
    itemPositionArray.forEach((itemPosition) => {
      let colorFromStop = getColorFromGradientWithPercentPosition(
        gradientArray,
        itemPosition
      );
      itemColorArray.push(colorFromStop);
    });

    let hexItemColorArray = [];
    itemColorArray.forEach((rgbColorArray) => {
      let hexColor = RGBToHex(
        rgbColorArray[0],
        rgbColorArray[1],
        rgbColorArray[2]
      );
      hexItemColorArray.push(hexColor);
    });

    sortedLeftRightItemArray.forEach((item, index) => {
      if (item.type === "shape") {
        item.style.fillColor = hexItemColorArray[index];
        // PUT OPACITY HERE WHEN MIRO SUPPORTS IT // rgbColorArray[3] (the 'a' channel of rgba)
        item.sync();
      }
    });
  }

  function handleKeyPress(e) {
    if (e.keyCode === 13) {
      e.target.blur();
      //Write you validation logic here
    }
  }

  async function createGradientFromBoardSelection() {
    // lets just start with 'shapes' for now.
    let viableItemTypes = ["shape"];
    let selectionArray = await miro.board.getSelection();
    let filteredArray = selectionArray.filter((item) =>
      viableItemTypes.includes(item.type)
    );
    let sortedFilteredArray = sortLeftRightTopBottom(filteredArray);
    if (sortedFilteredArray.length > 0) {
      let startPointX = sortedFilteredArray[0].x;
      let endPointX = sortedFilteredArray[sortedFilteredArray.length - 1].x;
      let width = endPointX - startPointX;

      let importedColorArray = [];
      if (sortedFilteredArray.length > 0) {
        sortedFilteredArray.forEach((item) => {
          let colorStop = {
            color: item.style.fillColor,
            position: ((item.x - startPointX) / width) * 100,
          };
          importedColorArray.push(colorStop);
        });
      }

      let startGradientString = "linear-gradient(90deg, ";
      let endGradientString = ")";
      let colorAndPositionMiddleOfGradientStringArray = [];
      importedColorArray.forEach((colorObject) => {
        let colorString = ` ${hexToRGB(colorObject.color)} ${colorObject.position
          }%`;
        colorAndPositionMiddleOfGradientStringArray.push(colorString);
      });
      let allColorString = colorAndPositionMiddleOfGradientStringArray.join();
      let finalGradientString = `${startGradientString}${allColorString}${endGradientString}`;

      handleSetGradientColor(finalGradientString);
    }
  }

  return (
    <div className="cs1 ce12 grid tool-container a-bit-of-shadow">
      {!dispatch ? (
        <>
          <div className="cs1 pl-6" style={{ opacity: 0 }} {...dragHandleProps}>
          </div>
          <div className="cs2 ce12">{nameFormValue}</div>
        </>
      ) : (
        <>
          <div className="cs1 pl-6" {...dragHandleProps}>
            <Bars style={{ fill: "currentColor" }} />
          </div>

          {/* <div className="cs2 ce8 p-medium">{gradient.name}</div> */}
          <input
            className="input input-small mb-0 cs2 ce8"
            type="text"
            placeholder="Clever Gradient Name"
            id="Gradient Name"
            value={nameFormValue}
            onChange={handleNameChange}
          />

          <div className="cs9 ce12 grid">
            <ToolTip
              placement={"top-end"}
              toolTipContent={
                <>
                  Save / export gradient. <br></br>(PRO: coming soon)
                </>
              }>
              <div className="cs1">
                <button
                  disabled={true}
                  onClick={() => console.log("DROP DOWN")}
                  className="cs1 button button-tertiary button-small smaller-button mb-0 mt-0 ml-4 mr-0 pl-0 pr-0 "
                  type="button">
                  <span className="icon-export mb-12"></span>
                </button>
              </div>
            </ToolTip>
            <ToolTip placement={"top-end"} toolTipContent={"Clone gradient"}>
              <button
                onClick={duplicateItem}
                className="button button-tertiary button-small smaller-button mb-0 mt-0 mr-4 ml-4 pl-0 pr-0 cs2 "
                type="button">
                <span className="icon-duplicate mb-12"></span>
              </button>
            </ToolTip>
            <ToolTip placement={"top-end"} toolTipContent={"Remove gradient"}>
              <button
                onClick={() => handleRemoveItem(item)}
                className="button button-tertiary button-small smaller-button mb-0 mt-0 mr-0 ml-0 pl-0 pr-0 cs3"
                type="button">
                <span className="icon-trash mb-8"></span>
              </button>
            </ToolTip>
          </div>
          <ToolTip placement={"top-end"} toolTipContent={"Gradient edit mode"}>
            <button
              onClick={() => handleSetEditMode()}
              className={
                "button button-tertiary button-small mb-0 mt-0 mr-0 ml-2 pl-1 pr-1 pr-0 cs1" +
                " " +
                (editMode ? "button-selected" : "")
              }
              type="button">
              <span className="icon-edit"></span>
            </button>
          </ToolTip>
        </>
      )}
      <ToolTip
        placement={"top-end"}
        toolTipContent={"Click to toggle gradient edit mode"}>
        <div
          className="cs2 ce8 gradient-strip cursor-pointer"
          onClick={() => setEditMode(!editMode)}
          style={{ background: item.gradient_colors }}></div>
      </ToolTip>
      <ToolTip
        placement={"top-end"}
        toolTipContent={
          <>
            Fill the selected board 'shapes' with gradient colors. Does not work
            on stickies, cards or text. <br />
            Fill order: left-right, top-bottom
          </>
        }>
        <div className="cs9 ce12 grid">
          <button
            disabled={!canFillSomeBoardItemsWithGradient}
            type="button"
            className="button button-secondary button-small cs1 ce12 mb-0 mr-0 pl-12"
            onClick={() =>
              fillItemsWithGradient(item.gradient_colors.toLowerCase())
            }>
            Fill Selected
          </button>
        </div>
      </ToolTip>

      <AnimateHeight
        id="example-panel"
        duration={1000}
        height={editMode ? "auto" : 0}
        className="cs1 ce12"
        easing="cubic-bezier(0, 1, 0.5, 1)">
        <div className="cs1 ce12 grid">
          <div className="cs1 ce12 grid color-picker-container pr-0 pl-0 pb-0 mt-8">
            <div className="cs1 ce12 mt-8 color-picker-fix ">
              <ColorPicker
                className="cs1 ce12 mr-0 ml-0 pl-0 pr-0 mt-0 pt-0"
                value={item.gradient_colors}
                hideControls={true}
                hidePresets={true}
                hideEyeDrop={true}
                hideAdvancedSliders={true}
                onChange={handleSetGradientColor}
              />
            </div>
          </div>
          <ToolTip
            placement={"top"}
            toolTipContent={"Create gradient from selected shapes."}>
            <div className="cs1 ce6">
              <button
                disabled={!canImportFromBoardSelection}
                className="cs1 ce6 button button-small button-secondary mb-8 mt-8 pl-8"
                onClick={createGradientFromBoardSelection}>
                <span className="icon icon-cursor-user mr-8 "></span>
                From selection
              </button>
            </div>
          </ToolTip>
          <ToolTip
            placement={"top"}
            toolTipContent={"Delete selected color point in gradient"}>
            <button
              className="cs7 ce12 button button-small button-secondary mb-8 mt-8"
              onClick={() => {
                deletePoint(selectedPoint);
              }}>
              <span className="icon  icon-trash mr-8"></span>
              Delete point
            </button>
          </ToolTip>
          {/* <div className="cs1 ce6 mt-0">
            <div className="p-medium cs1 ce12 mt-0 mb-4">
              <b>Where:</b>
            </div>
            <div className="cs1 ce12">
              <label className="radiobutton mt-0 mb-0">
                <input type="radio" name="radio" />
                <span>This Board</span>
              </label>
              <label className="radiobutton">
                <input type="radio" name="radio" />
                <span>Cloud</span>
              </label>
            </div>
          </div> */}
          {/* <div className="cs7 ce12 mt-0 mb-36">
            <div className="p-medium cs1 ce12 mt-0 mb-4">
              <b>Who:</b>
            </div>
            <div className="cs1 ce12">
              <label className="toggle mt-0 mb-0">
                <input type="checkbox" tabIndex="0" />
                <span>Team</span>
              </label>
            </div>
          </div> */}
          {/* <div className="form-group-small cs1 ce7 mt-0">
            
          </div>
          <button
            type="button"
            className="button button-small button-primary cs1 ce12 mr-0">
            Save Gradient
          </button> */}
        </div>
      </AnimateHeight>
    </div>
  );
}
